import { NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    DestroyRef,
    HostBinding,
    ViewChild,
    ViewContainerRef,
    afterNextRender,
    computed,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UrlSerializer, createUrlTreeFromSnapshot } from '@angular/router';

import {
    EpcotConfig,
    LayoutNavigationConfig,
    LiveSectionConfig,
    PrettyUrlsConfig,
    Sitecore,
    TopNavigationConfig,
} from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { TrackNavArea } from '@frontend/sports/navigation/core/feature/models';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { ScrollIosHackDirective, ScrollToDirective } from '@frontend/sports/web/directives-ui';
import { BottomNavigationStacksService, TopNavigationItemsProvider } from '@frontend/sports/web/navigation-data-access';
import { DsTouchClickDirective } from '@frontend/ui/shared';
import { MenuItemsService, PlainLinkComponent, ViewTransitionUtils } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { linkedTransferState } from '@frontend/vanilla/ssr';
import { isEqual, omit } from 'lodash-es';
import { combineLatest, filter, map, take } from 'rxjs';

import { BreadcrumbModel } from './breadcrumb.models';
import { BreadcrumbSlot, BreadcrumbsSlotService } from './breadcrumbs-slot.service';
import { Breadcrumbs, BreadcrumbsService } from './breadcrumbs.service';

@Component({
    selector: 'ms-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    imports: [
        ScrollIosHackDirective,
        ScrollToDirective,
        PlainLinkComponent,
        CompetitionLogoComponent,
        NgTemplateOutlet,
        DsTouchClickDirective,
        IconCustomComponent,
    ],
    host: {
        '[class.hidden]': 'hidden()',
    },
})
export class BreadcrumbsComponent implements AfterViewInit {
    private urlHelper = inject(UrlHelperService);
    private redirectHelper = inject(RedirectHelperService);
    private breadcrumbsSlot = inject(BreadcrumbsSlotService);
    private changeDetector = inject(ChangeDetectorRef);
    private trackingService = inject(TrackingService);
    private layoutNavigationConfig = inject(LayoutNavigationConfig);
    private menuService = inject(MenuItemsService);
    private liveConfig = inject(LiveSectionConfig);
    private topNavigationItemsProvider = inject(TopNavigationItemsProvider);
    private topNavigationConfig = inject(TopNavigationConfig);
    private routerEvents = inject(RouterEventsService);
    private sitecore = inject(Sitecore);
    protected urlConfig = inject(PrettyUrlsConfig);
    private bottomNavigationStacksService = inject(BottomNavigationStacksService);

    @HostBinding('class') className = 'breadcrumb';

    readonly hidden = linkedTransferState('MS_BREADCRUMBS_HIDDEN', true);

    @ViewChild('breadcrumbSlot', { read: ViewContainerRef, static: true })
    slotRef?: ViewContainerRef;

    items: BreadcrumbModel[] = [];
    hideMidSectionBreadCrumbItems: boolean;
    readonly minimalBreadcrumbs: boolean;
    readonly isEpcotEnabled = computed(() => this.epcotConfig.isEnabled);

    private routeHistory: { lastNonEdpUrl?: string; currentTag?: RouteTag } = {};

    private readonly _doc = inject(DOCUMENT);
    private readonly destroyRef = inject(DestroyRef);
    private readonly epcotConfig = inject(EpcotConfig);
    readonly #viewTransitionUtils = inject(ViewTransitionUtils);

    constructor() {
        const breadcrumbsSlot = this.breadcrumbsSlot;
        const sportsProductService = inject(SportsProductService);
        const breadcrumbService = inject(BreadcrumbsService);
        const adaptiveLayout = inject(AdaptiveLayoutService);
        const routerEvents = this.routerEvents;
        const urlSerializer = inject(UrlSerializer);

        breadcrumbsSlot.slotRegistered$.pipe(takeUntilDestroyed()).subscribe(this.initSlotComponent);

        const isBreadcrumbNavHidden$ = adaptiveLayout.stateChange$.pipe(
            takeUntilDestroyed(),
            map((state) => state && !state.breadcrumbNavigation),
        );
        const isSportsActive$ = sportsProductService.isSportsProductActive$;

        combineLatest([isBreadcrumbNavHidden$, isSportsActive$, routerEvents.currentActivationEnd.pipe(filter(isDefined), take(1))])
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady(), takeUntilDestroyed(this.destroyRef))
            .subscribe(([isBreadcrumbNavHidden, isSportsActive]) => this.hidden.set(isBreadcrumbNavHidden || !isSportsActive));

        this.hideMidSectionBreadCrumbItems = this.layoutNavigationConfig.hideMidSectionBreadCrumbItems;
        this.minimalBreadcrumbs = this.layoutNavigationConfig.useCleanNavigation;

        breadcrumbService.breadcrumbs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.breadcrumbChanged);

        const isEpcotToggleVisible = this.epcotConfig.isEnabled && (adaptiveLayout.current.headerSubNavigation || adaptiveLayout.current.columns < 3);

        routerEvents.currentActivationEnd.pipe(filter(isDefined), takeUntilDestroyed()).subscribe((e) => {
            this.routeHistory.currentTag = e.snapshot.data?.tag;

            if (isEpcotToggleVisible && e.snapshot.data?.tag !== RouteTag.EventDetails) {
                this.routeHistory.lastNonEdpUrl = urlSerializer.serialize(
                    createUrlTreeFromSnapshot(e.snapshot, [], omit(e.snapshot.queryParams, 'popup')),
                );
            }
        });

        afterNextRender(() => {
            this.topNavigationConfig.whenReady.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                if (this.liveConfig.showLiveBadgeCount.includes('BOTTOM')) {
                    this.setLiveCounts();
                }
            });
        });
    }

    ngAfterViewInit(): void {
        if (this.breadcrumbsSlot.registeredComponent) {
            this.initSlotComponent(this.breadcrumbsSlot.registeredComponent);
        } else {
            this.slotRef!.clear();
        }
    }

    goBack(): void {
        this.trackBackNavigation();

        if (this.bottomNavigationStacksService.isEnabled) {
            this.bottomNavigationStacksService.goBackStackScoped();
            return;
        }

        const fallbackUrl = this.isOnEventDetails() ? this.resolveEdpBackUrl() : undefined;

        if (this.bottomNavigationStacksService.isEnabled) {
            this.bottomNavigationStacksService.goBackStackScoped();
            return;
        }

        this.redirectHelper.goBack(fallbackUrl);
    }

    private isOnEventDetails(): boolean {
        return this.routeHistory.currentTag === RouteTag.EventDetails;
    }

    private trackBackNavigation(): void {
        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: `${TrackNavArea.NavBreadcrumb}_Back`,
        });
    }

    private resolveEdpBackUrl(): string | undefined {
        if (this.routeHistory.lastNonEdpUrl) {
            return this.routeHistory.lastNonEdpUrl;
        }

        const eventsUrl = `/${this.urlConfig.translations.events}`;
        // TODO: this is not available in the config
        // const cashierUrl = this.urlConfig.translations.cashier;
        // const excludedUrls = [eventsUrl, cashierUrl];
        const excludedUrls = [eventsUrl];

        const urlHistory = this.routerEvents.urlHistory ?? [];

        const backUrl = urlHistory.filter((url) => !excludedUrls.some((path) => decodeURI(url).includes(path))).pop();
        const homeUrl = this.urlHelper.getHomeUrl();

        return backUrl || homeUrl;
    }

    private breadcrumbChanged = (model: Breadcrumbs) => {
        if (!isEqual(this.items, model.breadcrumbs)) {
            this.items = model.breadcrumbs.map((crumb) => this.mapCrumb(crumb));

            if (this.minimalBreadcrumbs && this.items.length > 1) {
                const lastItem = this.items.at(-1);
                if (lastItem?.type === 'REGION') {
                    lastItem.title = `${this.sitecore.globalMessages.All} ${lastItem.title}`;
                }
                this.items = lastItem ? [lastItem] : [];
            } else if (this.hideMidSectionBreadCrumbItems && this.items.length > 2) {
                this.items.splice(1, this.items.length - 2);
            }
        }
    };

    selected(active: BreadcrumbModel): void {
        if (active.ignoreBrowserHistory && active.url) {
            this.redirectHelper.goToPage(active.url, active.ignoreBrowserHistory);
        }
    }

    private mapCrumb(crumb: BreadcrumbModel): BreadcrumbModel {
        return { ...crumb, url: this.urlHelper.getAbsolute(this.urlHelper.getUrl(crumb.url)) };
    }

    private initSlotComponent = (data?: BreadcrumbSlot): void => {
        if (!this.slotRef) {
            return;
        }

        this.slotRef.clear();

        if (!data) {
            return;
        }

        const component = this.slotRef.createComponent<any>(data.component);

        if (data.inputs) {
            Object.keys(data.inputs).forEach((key) => (component.instance[key] = data.inputs[key]));
        }

        this.changeDetector.detectChanges();
    };

    private setBadgeBottom(itemName: string, badgeText: string | undefined, cssClass?: string): void {
        this.menuService.setCounter(TrackNavArea.NavBottom, itemName, badgeText, cssClass);
    }

    private refreshLiveCount() {
        this.topNavigationItemsProvider
            .getLiveSportsList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((liveCount) => {
                const elements = this._doc.querySelectorAll('.live-counts-refresh');
                if (elements.length > 0) {
                    [...elements].forEach((element) => {
                        element.innerHTML = this.topNavigationItemsProvider.formatLiveCounts(liveCount);
                    });
                }
            });
    }

    private setLiveCounts() {
        this.topNavigationItemsProvider
            .getLiveSportsList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((liveCount) => {
                const count = this.topNavigationItemsProvider.formatLiveCounts(liveCount);
                const className = this.getClassName(count);
                this.setBadgeBottom('live', count, `live-counts-refresh bottomnav-new-badge-live-count ${className}`);
            });
        this.refreshLiveCountTimer();
    }

    private getClassName(count: string): string {
        switch (count.length) {
            case 2:
                return 'livebadge-fontsize-sm';
            case 1:
                return 'livebadge-fontsize-md';
            case 0:
                return 'livebadge-fontsize-lg';
            default:
                return 'livebadge-fontsize-default';
        }
    }

    private refreshLiveCountTimer() {
        const timeout = setInterval(() => {
            this.refreshLiveCount();
        }, 60_000);
        this.destroyRef.onDestroy(() => clearTimeout(timeout));
    }
}
